import { useCallback, useMemo } from "react";
import { Flex, Text, IconButton, Box } from "@radix-ui/themes";
import { TrashIcon } from "lucide-react";
import Select from "@/components/shared/select";
import { Input } from "@/components/ui/input";
import Switch from "@/components/shared/inputs/switch";
import postAnswerStyles from "./PostAnswerActionsConfiguration.module.scss";
import { CustomFieldRowProps } from "./PostAnswerActions.model";
import { FieldReferenceInput } from "./FieldReferenceInput";

export function CustomFieldRow({
    index,
    fieldId,
    value,
    availableFields,
    currentField,
    fieldReferenceOptions,
    onUpdate,
    onRemove,
    error,
}: CustomFieldRowProps) {
    const fieldOptions = useMemo(() => {
        const options = availableFields.map((f) => ({
            value: f.id,
            label: f.label,
        }));

        if (currentField && !availableFields.find((f) => f.id === currentField.id)) {
            options.push({
                value: currentField.id,
                label: currentField.label,
            });
        }

        return options.sort((a, b) => a.label.localeCompare(b.label));
    }, [availableFields, currentField]);

    const handleValueChange = useCallback(
        (newValue: string | string[]) => {
            onUpdate(index, fieldId, newValue);
        },
        [index, fieldId, onUpdate]
    );

    const renderValueInput = useCallback(() => {
        if (!currentField) {
            return (
                <Input
                    disabled
                    placeholder="Select a field first..."
                    className={postAnswerStyles.customFieldInput}
                />
            );
        }

        switch (currentField.type) {
            case "select":
                return (
                    <FieldReferenceInput
                        value={value}
                        onChange={handleValueChange}
                        fieldReferenceOptions={fieldReferenceOptions}
                        selectOptions={currentField.options || []}
                        placeholder="Select a value..."
                    />
                );

            case "multiselect":
                return (
                    <FieldReferenceInput
                        value={value}
                        onChange={(v) => {
                            if (v.startsWith("@[")) {
                                handleValueChange(v);
                                return;
                            }
                            handleValueChange(v ? [v] : []);
                        }}
                        fieldReferenceOptions={fieldReferenceOptions}
                        selectOptions={currentField.options || []}
                        placeholder="Select values..."
                    />
                );

            case "checkbox":
                return (
                    <Flex align="center" gap="2">
                        <Text size="2">{currentField.label}</Text>
                        <Flex className={postAnswerStyles.customFieldSwitch}>
                            <Switch
                                value={value === "true"}
                                onChange={(checked) => handleValueChange(checked ? "true" : "false")}
                            />
                        </Flex>
                    </Flex>
                );

            case "number":
                return (
                    <Input
                        type="number"
                        placeholder="Enter a number..."
                        value={(value as string) || ""}
                        onChange={(e) => handleValueChange(e.target.value)}
                        className={postAnswerStyles.inputField}
                    />
                );

            case "date":
                return (
                    <Input
                        type="date"
                        value={(value as string) || ""}
                        onChange={(e) => handleValueChange(e.target.value)}
                        className={postAnswerStyles.inputField}
                    />
                );

            case "text":
            default:
                return (
                    <FieldReferenceInput
                        value={value}
                        onChange={handleValueChange}
                        fieldReferenceOptions={fieldReferenceOptions}
                        placeholder="Enter a value..."
                    />
                );
        }
    }, [currentField, value, handleValueChange, fieldReferenceOptions]);

    return (
        <Flex direction="column" gap="2" className={postAnswerStyles.customFieldRow}>
            <Flex gap="3" align="center">
                <Box className={postAnswerStyles.customFieldSelectBox}>
                    <Select
                        options={fieldOptions}
                        value={fieldId || undefined}
                        onChange={(newFieldId) => { onUpdate(index, newFieldId, "") }}
                        placeholder="Select a field..."
                        allowSearch
                    />
                </Box>

                <Box className={postAnswerStyles.customFieldValueBox}>{renderValueInput()}</Box>

                <IconButton
                    highContrast
                    variant="ghost"
                    color="gray"
                    size="2"
                    onClick={() => onRemove(index)}
                    aria-label="Remove custom field"
                >
                    <TrashIcon size={16} />
                </IconButton>
            </Flex>

            {error && (
                <Text size="1" color="red">
                    {error}
                </Text>
            )}
        </Flex>
    );
}

